// client/src/pages/LogicAppReport.tsx
//
// Logic App Health — live view backed by the Azure Management REST API
// (api/logicapp-health), no Snowflake/data-lake dependency.
//
//   overview grid  : per-app run summary over the last N days (cached server-side)
//   drill-down     : pick a run → per-step timings, slowest + failed steps
//   documentation  : team-tools profile (structured header + full markdown)
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Activity, ExternalLink, RefreshCw, Clock, XCircle, AlertTriangle,
  Search, X, ChevronRight, Timer, Zap, FileText, ListChecks, Database,
} from 'lucide-react';

// ---------- types ----------
type AppSummary = {
  name: string;
  state?: string;
  runCount: number;
  succeeded?: number;
  failed?: number;
  running?: number;
  cancelled?: number;
  failureRate?: number;
  maxDurationSec?: number | null;
  avgDurationSec?: number | null;
  isLongRunning?: boolean;
  longestRun?: { runId: string; status: string; startTime: string; durationSec: number | null } | null;
  lastRun?: { runId: string; status: string; startTime: string; durationSec: number | null } | null;
  latestFailedRunId?: string | null;
  portalLink?: string;
  schedule?: string | null;
  summary?: string | null;
  hasDoc?: boolean;
  error?: string;
};

type Overview = {
  generatedAt: string;
  days: number;
  since: string;
  fromCache?: boolean;
  cacheAgeSec?: number;
  totals: {
    apps: number; runs: number; failed: number; running: number;
    appsWithFailures: number; appsLongRunning: number; longestRunSec: number;
  };
  apps: AppSummary[];
};

type RunRow = {
  runId: string; status: string; code?: string | null;
  startTime: string | null; endTime: string | null;
  durationSec: number | null; error?: string | null;
};

type Step = {
  name: string; status: string; code?: string | null;
  durationSec: number | null; startTime: string | null; endTime: string | null;
  error?: string | null; inputsLink?: string | null; outputsLink?: string | null;
};

type ActionsResult = {
  workflow: string; runId: string; stepCount: number;
  slowestStep: Step | null; failedSteps: Step[]; steps: Step[];
};

type DocResult = {
  workflow: string; found: boolean;
  schedule?: string | null; summary?: string | null;
  structured?: Record<string, any> | null; markdown?: string | null;
};

// ---------- helpers ----------
const DAY_OPTIONS = [1, 2, 7, 14];

function statusColor(status?: string): string {
  switch ((status || '').toLowerCase()) {
    case 'succeeded': return 'var(--good)';
    case 'failed': return 'var(--bad)';
    case 'running': return 'var(--accent)';
    case 'cancelled': return '#fbbf24';
    case 'skipped': return '#6b7280';
    case 'timedout': return '#f59e0b';
    default: return 'var(--muted)';
  }
}

function fmtDur(sec?: number | null): string {
  if (sec == null) return '—';
  if (sec < 1) return '<1s';
  if (sec < 60) return `${Math.round(sec)}s`;
  if (sec < 3600) { const m = Math.floor(sec / 60); const s = Math.round(sec % 60); return s ? `${m}m ${s}s` : `${m}m`; }
  const h = Math.floor(sec / 3600); const m = Math.round((sec % 3600) / 60); return m ? `${h}h ${m}m` : `${h}h`;
}

function fmtWhen(iso?: string | null): string {
  if (!iso) return '—';
  const t = Date.parse(iso); if (!t) return '—';
  const diff = (Date.now() - t) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

async function getJson<T>(url: string, signal?: AbortSignal): Promise<T> {
  const r = await fetch(url, { credentials: 'include', signal });
  if (!r.ok) {
    let msg = `HTTP ${r.status}`;
    try { const b = await r.json(); if (b?.error) msg = b.error; } catch { /* ignore */ }
    throw new Error(msg);
  }
  return r.json();
}

// ---------- small UI bits ----------
function StatusPill({ status }: { status?: string }) {
  const c = statusColor(status);
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 600,
      color: c, background: `color-mix(in srgb, ${c} 14%, transparent)`,
      padding: '2px 8px', borderRadius: 999, whiteSpace: 'nowrap',
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: c, display: 'inline-block' }} />
      {status || 'Unknown'}
    </span>
  );
}

function Kpi({ label, value, accent, icon }: { label: string; value: React.ReactNode; accent?: string; icon?: React.ReactNode }) {
  return (
    <div className="card" style={{ padding: '12px 20px', minWidth: 120, textAlign: 'center', borderLeft: accent ? `3px solid ${accent}` : undefined }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
        {icon}
        <span style={{ fontSize: 24, fontWeight: 700, lineHeight: 1, color: accent }}>{value}</span>
      </div>
      <div className="muted" style={{ fontSize: 12, marginTop: 5 }}>{label}</div>
    </div>
  );
}

// ---------- overview card ----------
function AppCard({ app, onOpen }: { app: AppSummary; onOpen: (a: AppSummary) => void }) {
  const enabled = (app.state || '').toLowerCase() === 'enabled';
  const failed = app.failed || 0;
  const running = app.running || 0;
  const errored = !!app.error;
  const accent = errored ? '#f59e0b' : failed > 0 ? 'var(--bad)' : app.isLongRunning ? '#fbbf24' : 'var(--good)';
  const successRate = app.runCount > 0 ? Math.round(((app.succeeded || 0) / app.runCount) * 100) : null;

  return (
    <div className="card" role="button" tabIndex={0}
      onClick={() => onOpen(app)}
      onKeyDown={(e) => { if (e.key === 'Enter') onOpen(app); }}
      style={{ padding: 0, borderLeft: `3px solid ${accent}`, overflow: 'hidden', cursor: 'pointer' }}>
      <div style={{ padding: '14px 16px 10px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <span style={{
            width: 8, height: 8, borderRadius: '50%', marginTop: 5, flexShrink: 0,
            background: enabled ? 'var(--good)' : '#6b7280', boxShadow: enabled ? '0 0 6px #57d38c88' : 'none',
          }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 13.5, wordBreak: 'break-word' }}>{app.name}</div>
            {app.schedule && <div className="muted" style={{ fontSize: 11.5, marginTop: 3, display: 'flex', alignItems: 'center', gap: 5 }}><Clock size={11} />{app.schedule}</div>}
          </div>
          {app.portalLink && (
            <a href={app.portalLink} target="_blank" rel="noreferrer" className="icon-btn" title="Open in Azure Portal"
              onClick={(e) => e.stopPropagation()} style={{ flexShrink: 0, padding: 4 }}>
              <ExternalLink size={13} />
            </a>
          )}
        </div>
      </div>

      {errored ? (
        <div style={{ padding: '8px 16px 14px', fontSize: 11.5, color: '#f59e0b', display: 'flex', gap: 6, alignItems: 'center' }}>
          <AlertTriangle size={13} /> {app.error}
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)' }}>
            <Cell label="Runs" value={app.runCount} />
            <Cell label="Failed" value={failed} color={failed > 0 ? 'var(--bad)' : undefined} />
            <Cell label="Max dur" value={fmtDur(app.maxDurationSec)} color={app.isLongRunning ? '#fbbf24' : undefined} small />
            <Cell label="Success" value={successRate != null ? `${successRate}%` : '—'}
              color={successRate == null ? undefined : successRate === 100 ? 'var(--good)' : successRate >= 90 ? '#fbbf24' : 'var(--bad)'} small />
          </div>
          <div style={{ padding: '9px 16px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
              {app.lastRun ? <StatusPill status={app.lastRun.status} /> : <span className="muted" style={{ fontSize: 11 }}>no runs in window</span>}
              {running > 0 && <span style={{ fontSize: 11, color: 'var(--accent)' }}>{running} running</span>}
              {app.lastRun && <span className="muted" style={{ fontSize: 11 }}>{fmtWhen(app.lastRun.startTime)}</span>}
            </div>
            <ChevronRight size={15} style={{ color: 'var(--muted)', flexShrink: 0 }} />
          </div>
        </>
      )}
    </div>
  );
}

function Cell({ label, value, color, small }: { label: string; value: React.ReactNode; color?: string; small?: boolean }) {
  return (
    <div style={{ flex: 1, padding: '8px 6px', borderRight: '1px solid var(--border)', textAlign: 'center' }}>
      <div style={{ fontSize: small ? 14 : 17, fontWeight: 700, lineHeight: 1, color }}>{value}</div>
      <div className="muted" style={{ fontSize: 10, marginTop: 4 }}>{label}</div>
    </div>
  );
}

// ---------- drill-down: steps ----------
function StepRow({ s, slowest }: { s: Step; slowest: boolean }) {
  const c = statusColor(s.status);
  const isFail = (s.status || '').toLowerCase() === 'failed' || (s.status || '').toLowerCase() === 'timedout';
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, padding: '8px 10px',
      borderRadius: 8, background: slowest ? 'color-mix(in srgb, #fbbf24 10%, transparent)' : isFail ? 'color-mix(in srgb, var(--bad) 8%, transparent)' : 'transparent',
      border: slowest ? '1px solid #fbbf2455' : '1px solid transparent',
    }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          {slowest && <Zap size={12} style={{ color: '#fbbf24' }} />}
          <span style={{ fontSize: 12.5, fontWeight: 600, wordBreak: 'break-word' }}>{s.name.replace(/_/g, ' ')}</span>
          <StatusPill status={s.status} />
          {s.code && s.code !== 'OK' && s.code !== 'NotSpecified' && (
            <span style={{ fontSize: 10.5, color: c }}>{s.code}</span>
          )}
        </div>
        {s.error && <div style={{ fontSize: 11, color: 'var(--bad)', marginTop: 3 }}>{s.error}</div>}
      </div>
      <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: slowest ? '#fbbf24' : undefined }}>{fmtDur(s.durationSec)}</div>
      </div>
    </div>
  );
}

function StepBreakdown({ workflow, runId }: { workflow: string; runId: string }) {
  const [data, setData] = useState<ActionsResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true); setErr(null); setData(null);
    getJson<ActionsResult>(`/api/logicapp-health?mode=actions&workflow=${encodeURIComponent(workflow)}&runId=${encodeURIComponent(runId)}`, ctrl.signal)
      .then((d) => { setData(d); setLoading(false); })
      .catch((e) => { if (e.name !== 'AbortError') { setErr(e.message); setLoading(false); } });
    return () => ctrl.abort();
  }, [workflow, runId]);

  if (loading) return <div className="muted" style={{ fontSize: 12, padding: 12 }}>Loading step timings…</div>;
  if (err) return <div style={{ color: 'var(--bad)', fontSize: 12, padding: 12 }}>Error: {err}</div>;
  if (!data) return null;

  const slowestName = data.slowestStep?.name;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {data.slowestStep && (
        <div style={{ fontSize: 12, color: 'var(--muted)' }}>
          Slowest step: <strong style={{ color: '#fbbf24' }}>{data.slowestStep.name.replace(/_/g, ' ')}</strong> — {fmtDur(data.slowestStep.durationSec)}
          {data.failedSteps.length > 0 && <span> · <span style={{ color: 'var(--bad)' }}>{data.failedSteps.length} failed step{data.failedSteps.length > 1 ? 's' : ''}</span></span>}
          <span> · {data.stepCount} steps total</span>
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {data.steps.filter((s) => s.durationSec != null && (s.durationSec >= 0.5 || s.status === 'Failed')).map((s) => (
          <StepRow key={s.name} s={s} slowest={s.name === slowestName} />
        ))}
      </div>
    </div>
  );
}

// ---------- drill-down: runs tab ----------
function RunsTab({ app, days }: { app: AppSummary; days: number }) {
  const [runs, setRuns] = useState<RunRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);

  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true); setErr(null);
    getJson<{ runs: RunRow[] }>(`/api/logicapp-health?mode=runs&workflow=${encodeURIComponent(app.name)}&days=${days}`, ctrl.signal)
      .then((d) => {
        const rows = d.runs || [];
        setRuns(rows);
        // auto-select the most interesting run: latest failed, else longest
        const failed = rows.find((r) => r.status === 'Failed');
        const longest = [...rows].sort((a, b) => (b.durationSec ?? -1) - (a.durationSec ?? -1))[0];
        setSelected((failed || longest || rows[0])?.runId || null);
        setLoading(false);
      })
      .catch((e) => { if (e.name !== 'AbortError') { setErr(e.message); setLoading(false); } });
    return () => ctrl.abort();
  }, [app.name, days]);

  if (loading) return <div className="muted" style={{ fontSize: 12, padding: 12 }}>Loading runs…</div>;
  if (err) return <div style={{ color: 'var(--bad)', fontSize: 12, padding: 12 }}>Error: {err}</div>;
  if (!runs || runs.length === 0) return <div className="muted" style={{ fontSize: 12, padding: 12 }}>No runs in the last {days} day(s).</div>;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: 14, minHeight: 0 }}>
      {/* run list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 460, overflowY: 'auto', paddingRight: 4 }}>
        {runs.map((r) => {
          const sel = r.runId === selected;
          return (
            <button key={r.runId} type="button" onClick={() => setSelected(r.runId)}
              style={{
                textAlign: 'left', padding: '8px 10px', borderRadius: 8, cursor: 'pointer',
                border: `1px solid ${sel ? 'var(--accent)' : 'var(--border)'}`,
                background: sel ? 'color-mix(in srgb, var(--accent) 12%, transparent)' : 'transparent',
                display: 'flex', flexDirection: 'column', gap: 4,
              }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6 }}>
                <StatusPill status={r.status} />
                <span style={{ fontSize: 12, fontWeight: 700, color: (r.durationSec || 0) >= 600 ? '#fbbf24' : undefined }}>
                  <Timer size={10} style={{ marginRight: 3 }} />{fmtDur(r.durationSec)}
                </span>
              </div>
              <span className="muted" style={{ fontSize: 10.5 }}>{fmtWhen(r.startTime)} · {r.startTime?.replace('T', ' ').slice(0, 16)}</span>
            </button>
          );
        })}
      </div>
      {/* step breakdown for selected run */}
      <div style={{ minWidth: 0 }}>
        {selected ? <StepBreakdown workflow={app.name} runId={selected} /> : <div className="muted">Select a run.</div>}
      </div>
    </div>
  );
}

// ---------- drill-down: documentation tab ----------
function DocField({ label, value }: { label: string; value: React.ReactNode }) {
  if (value == null || value === '' || (Array.isArray(value) && value.length === 0)) return null;
  return (
    <div style={{ display: 'flex', gap: 8, fontSize: 12, padding: '3px 0' }}>
      <span className="muted" style={{ minWidth: 130, flexShrink: 0 }}>{label}</span>
      <span style={{ wordBreak: 'break-word' }}>{Array.isArray(value) ? value.join(', ') : value}</span>
    </div>
  );
}

function DocTab({ app }: { app: AppSummary }) {
  const [doc, setDoc] = useState<DocResult | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    getJson<DocResult>(`/api/logicapp-health?mode=doc&workflow=${encodeURIComponent(app.name)}`, ctrl.signal)
      .then((d) => { setDoc(d); setLoading(false); })
      .catch((e) => { if (e.name !== 'AbortError') { setLoading(false); } });
    return () => ctrl.abort();
  }, [app.name]);

  if (loading) return <div className="muted" style={{ fontSize: 12, padding: 12 }}>Loading documentation…</div>;
  if (!doc || !doc.found) return <div className="muted" style={{ fontSize: 12, padding: 12 }}>No team-tools profile bundled for this app.</div>;

  const s = doc.structured || {};
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* structured header */}
      <div className="card" style={{ padding: '12px 14px' }}>
        {doc.summary && <div style={{ fontSize: 12.5, marginBottom: 8 }}>{doc.summary}</div>}
        <DocField label="Tag purpose" value={s.tagPurpose} />
        <DocField label="Source" value={s.sourceSystem} />
        <DocField label="Destinations" value={s.destinations} />
        <DocField label="Frequency" value={s.frequency} />
        <DocField label="Method" value={s.method} />
        <DocField label="Business owner" value={s.businessOwner} />
        <DocField label="Technical owner" value={s.technicalOwner} />
        <DocField label="Activity tier" value={s.tier} />
        <DocField label="Connections" value={(s.connections || []).map((c: any) => c.label || c.name)} />
        <DocField label="External hosts" value={s.externalHosts} />
        <DocField label="Last updated" value={s.lastUpdated} />
        {Array.isArray(s.recommendations) && s.recommendations.length > 0 && (
          <div style={{ marginTop: 8, fontSize: 12 }}>
            <span className="muted">Recommendations:</span>
            <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>{s.recommendations.map((r: any, i: number) => <li key={i}>{typeof r === 'string' ? r : JSON.stringify(r)}</li>)}</ul>
          </div>
        )}
      </div>
      {/* full markdown */}
      {doc.markdown && (
        <div className="card" style={{ padding: '14px 18px' }}>
          <div className="logicapp-md" style={{ fontSize: 12.5, lineHeight: 1.6 }}>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{doc.markdown}</ReactMarkdown>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- drill-down drawer ----------
function DrillDrawer({ app, days, onClose }: { app: AppSummary; days: number; onClose: () => void }) {
  const [tab, setTab] = useState<'runs' | 'doc'>('runs');
  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [onClose]);

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 1000, display: 'flex', justifyContent: 'flex-end' }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        width: 'min(820px, 94vw)', height: '100%', background: 'var(--panel, #11161f)',
        borderLeft: '1px solid var(--border)', boxShadow: '-8px 0 30px rgba(0,0,0,.4)',
        display: 'flex', flexDirection: 'column',
      }}>
        {/* header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 16, fontWeight: 700, wordBreak: 'break-word' }}>{app.name}</div>
              {app.schedule && <div className="muted" style={{ fontSize: 12, marginTop: 3, display: 'flex', alignItems: 'center', gap: 5 }}><Clock size={11} />{app.schedule}</div>}
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              {app.portalLink && <a href={app.portalLink} target="_blank" rel="noreferrer" className="btn" style={{ fontSize: 12, gap: 5 }}><ExternalLink size={13} /> Azure</a>}
              <button className="icon-btn" onClick={onClose} title="Close"><X size={16} /></button>
            </div>
          </div>
          {/* quick stats */}
          <div style={{ display: 'flex', gap: 16, marginTop: 12, flexWrap: 'wrap', fontSize: 12 }}>
            <span><strong>{app.runCount}</strong> <span className="muted">runs</span></span>
            <span style={{ color: (app.failed || 0) > 0 ? 'var(--bad)' : undefined }}><strong>{app.failed || 0}</strong> <span className="muted">failed</span></span>
            <span><strong>{fmtDur(app.maxDurationSec)}</strong> <span className="muted">max</span></span>
            <span><strong>{fmtDur(app.avgDurationSec)}</strong> <span className="muted">avg</span></span>
          </div>
          {/* tabs */}
          <div style={{ display: 'flex', gap: 4, marginTop: 14 }}>
            <TabBtn active={tab === 'runs'} onClick={() => setTab('runs')} icon={<ListChecks size={13} />}>Runs &amp; steps</TabBtn>
            <TabBtn active={tab === 'doc'} onClick={() => setTab('doc')} icon={<FileText size={13} />}>Documentation</TabBtn>
          </div>
        </div>
        {/* body */}
        <div style={{ padding: 20, overflowY: 'auto', flex: 1 }}>
          {tab === 'runs' ? <RunsTab app={app} days={days} /> : <DocTab app={app} />}
        </div>
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, icon, children }: { active: boolean; onClick: () => void; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <button type="button" onClick={onClick} style={{
      display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
      padding: '7px 12px', borderRadius: 8, border: '1px solid transparent',
      color: active ? 'var(--accent)' : 'var(--muted)',
      background: active ? 'color-mix(in srgb, var(--accent) 12%, transparent)' : 'transparent',
    }}>{icon}{children}</button>
  );
}

// ---------- definition search ----------
type SearchHit = {
  name: string;
  description: string | null;
  status: string | null;
  businessProcess: string | null;
  department: string | null;
  reviewDate: string | null;
  portalLink: string;
  snippets: string[];
  matchingActions: { name: string; type: string }[];
};

type SearchResult = {
  q: string; action: string; total: number; count: number; results: SearchHit[];
};

function DefSearch() {
  const [input, setInput] = useState('');
  const [mode, setMode] = useState<'text' | 'action'>('text');
  const [result, setResult] = useState<SearchResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(async () => {
    const q = input.trim();
    if (!q) return;
    setLoading(true); setError(null); setResult(null);
    const param = mode === 'action' ? `action=${encodeURIComponent(q)}` : `q=${encodeURIComponent(q)}`;
    try {
      const r = await getJson<SearchResult>(`/api/logicapp-health?mode=search&${param}`);
      setResult(r);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [input, mode]);

  const onKey = (e: React.KeyboardEvent) => { if (e.key === 'Enter') run(); };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* search bar */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 2, background: 'var(--border)', padding: 2, borderRadius: 8 }}>
          {(['text', 'action'] as const).map((m) => (
            <button key={m} type="button" onClick={() => setMode(m)} style={{
              fontSize: 12, fontWeight: 600, padding: '5px 11px', borderRadius: 6, cursor: 'pointer', border: 'none',
              background: mode === m ? 'var(--panel, #11161f)' : 'transparent',
              color: mode === m ? 'var(--accent)' : 'var(--muted)',
            }}>{m === 'text' ? 'Text' : 'Action type'}</button>
          ))}
        </div>
        <div style={{ position: 'relative', flex: '1 1 280px', maxWidth: 480 }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--muted)' }} />
          <input
            value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={onKey}
            placeholder={mode === 'action' ? 'e.g. Http, SqlServerExecuteStoredProcedure, ServiceProvider…' : 'Search text in all Logic App definitions…'}
            style={{ width: '100%', padding: '8px 10px 8px 32px', borderRadius: 8, border: '1px solid var(--border)', background: 'transparent', color: 'inherit', fontSize: 13 }}
          />
        </div>
        <button className="btn" onClick={run} disabled={loading || !input.trim()} style={{ gap: 6, fontSize: 12.5 }}>
          {loading ? <RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Search size={14} />}
          {loading ? 'Searching…' : 'Search'}
        </button>
      </div>

      {/* hint */}
      {!result && !loading && !error && (
        <div className="muted" style={{ fontSize: 12.5, lineHeight: 1.6 }}>
          <strong>Text</strong> — searches the full JSON definition of every Logic App (like <code>WHERE json LIKE '%term%'</code>).<br />
          <strong>Action type</strong> — finds apps that contain a specific action type anywhere in their action tree.
        </div>
      )}

      {error && <div style={{ color: 'var(--bad)', fontSize: 13 }}>Error: {error}</div>}

      {result && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div className="muted" style={{ fontSize: 12.5 }}>
            {result.count} of {result.total} Logic Apps match
            {result.q ? <> &ldquo;<strong style={{ color: 'inherit' }}>{result.q}</strong>&rdquo;</> : null}
            {result.action ? <> action type <strong style={{ color: 'inherit' }}>{result.action}</strong></> : null}
          </div>
          {result.results.length === 0 && (
            <div className="muted" style={{ textAlign: 'center', padding: '30px 0' }}>No Logic Apps matched.</div>
          )}
          {result.results.map((hit) => (
            <SearchHitCard key={hit.name} hit={hit} searchTerm={result.q} />
          ))}
        </div>
      )}
    </div>
  );
}

function SearchHitCard({ hit, searchTerm }: { hit: SearchHit; searchTerm: string }) {
  const [open, setOpen] = useState(false);
  const statusColor = hit.status === 'active' ? 'var(--good)' : hit.status === 'inactive' ? '#6b7280' : 'var(--muted)';

  return (
    <div className="card" style={{ padding: '12px 16px', borderLeft: '3px solid var(--accent)' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 700, fontSize: 13.5 }}>{hit.name}</span>
            {hit.status && (
              <span style={{ fontSize: 10.5, fontWeight: 600, color: statusColor,
                background: `color-mix(in srgb, ${statusColor} 14%, transparent)`,
                padding: '1px 7px', borderRadius: 999 }}>{hit.status}</span>
            )}
            {hit.department && <span className="muted" style={{ fontSize: 11 }}>{hit.department}</span>}
            {hit.businessProcess && <span className="muted" style={{ fontSize: 11 }}>· {hit.businessProcess}</span>}
          </div>
          {hit.description && <div style={{ fontSize: 12, marginTop: 4, color: 'var(--muted)' }}>{hit.description}</div>}
        </div>
        <a href={hit.portalLink} target="_blank" rel="noreferrer" className="icon-btn" title="Open in Azure Portal"
          onClick={(e) => e.stopPropagation()} style={{ flexShrink: 0, padding: 4 }}>
          <ExternalLink size={13} />
        </a>
      </div>

      {/* snippets */}
      {hit.snippets.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <button type="button" onClick={() => setOpen(!open)}
            style={{ fontSize: 11.5, color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', gap: 5 }}>
            <ChevronRight size={12} style={{ transform: open ? 'rotate(90deg)' : undefined, transition: 'transform .15s' }} />
            {hit.snippets.length} match context{hit.snippets.length > 1 ? 'es' : ''}
          </button>
          {open && hit.snippets.map((s, i) => (
            <SnippetBlock key={i} text={s} term={searchTerm} />
          ))}
        </div>
      )}

      {/* matching actions */}
      {hit.matchingActions.length > 0 && (
        <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 5 }}>
          {hit.matchingActions.map((a) => (
            <span key={a.name} title={a.type} style={{
              fontSize: 11, fontWeight: 500, padding: '2px 8px', borderRadius: 999,
              background: 'color-mix(in srgb, var(--accent) 12%, transparent)', color: 'var(--accent)',
            }}>{a.name}</span>
          ))}
        </div>
      )}
    </div>
  );
}

function SnippetBlock({ text, term }: { text: string; term: string }) {
  const lower = text.toLowerCase();
  const tl = term.toLowerCase();
  const idx = lower.indexOf(tl);
  if (idx === -1 || !term) {
    return <pre style={{ fontSize: 10.5, margin: '4px 0', padding: '6px 10px', borderRadius: 6, background: 'rgba(255,255,255,.04)', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{text}</pre>;
  }
  return (
    <pre style={{ fontSize: 10.5, margin: '4px 0', padding: '6px 10px', borderRadius: 6, background: 'rgba(255,255,255,.04)', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
      {text.slice(0, idx)}
      <mark style={{ background: 'color-mix(in srgb, #fbbf24 40%, transparent)', color: 'inherit', borderRadius: 2 }}>{text.slice(idx, idx + term.length)}</mark>
      {text.slice(idx + term.length)}
    </pre>
  );
}

// ---------- SQL call-site catalog ----------
type SqlCallSite = {
  workflow: string; action: string; state: string; inLoop: boolean;
  runCount?: number; maxPerRun?: number;
};

type SqlGroup = {
  fingerprint: string;
  statement: string | null;
  tables: string[];
  exampleText: string;
  exampleTruncated?: boolean;
  textClasses: string[];
  anyExact: boolean;
  callSites: number;
  enabledCallSites: number;
  inLoopCallSites: number;
  ambiguousCallSites: number;
  databases: string[];
  distinctWorkflows: number;
  perWorkflowMaxPerRun: Record<string, number>;
  maxPerRun: number;
  exclusiveCallSites: number;
  estExecutions?: number;
  estIfEveryBranchFired?: number;
  estIsLowerBound?: boolean;
  estTruncated?: boolean;
  workflows: SqlCallSite[];
};

type SqlWorkflowVolume = {
  workflow: string; state: string; callSites: number; maxPerRun: number;
  exclusiveCallSites: number; inLoopCallSites: number;
  databases: string[]; runCount: number; runCountTruncated: boolean;
  estExecutions: number; estIfEveryBranchFired: number; estIsLowerBound: boolean;
};

type SqlCatalogResult = {
  generatedAt: string;
  fromCache?: boolean;
  cacheAgeSec?: number;
  workflowsScanned: number;
  totals: {
    callSites: number; workflowsWithSql: number; enabledCallSites: number;
    inLoopCallSites: number; distinctShapes: number; exactTextCallSites: number;
    unresolvedCallSites: number; maxPerRunCallSites: number;
    exclusiveCallSites: number; dispatcherWorkflows: number;
  };
  databases: { database: string; callSites: number; enabledCallSites: number; workflows: number }[];
  byTextClass: Record<string, number>;
  dbFilter: string | null;
  volume: { days: number; since: string; basis: string; caveats: string[] } | null;
  workflowVolume: SqlWorkflowVolume[] | null;
  groupCount: number;
  groups: SqlGroup[];
  unresolved: { workflow: string; action: string; textClass: string; database: string }[];
};

const TEXT_CLASS_LABEL: Record<string, string> = {
  'literal': 'exact — literal in action',
  'hop-compose-literal': 'exact — via Compose',
  'hop-variable-single': 'exact — via variable',
  'inline-interpolated': 'template — inline @{…}',
  'hop-compose-interpolated': 'template — via Compose',
  'hop-variable-multi': 'ambiguous — variable reassigned',
};

function Badge({ children, color, title }: { children: React.ReactNode; color: string; title?: string }) {
  return (
    <span title={title} style={{
      fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 999, whiteSpace: 'nowrap',
      color, background: `color-mix(in srgb, ${color} 15%, transparent)`, border: `1px solid color-mix(in srgb, ${color} 35%, transparent)`,
    }}>{children}</span>
  );
}

function SqlBlock({ text, truncated }: { text: string; truncated?: boolean }) {
  const [open, setOpen] = useState(false);
  const long = text.length > 320;
  return (
    <div>
      <pre style={{
        margin: '6px 0 0', padding: '8px 10px', borderRadius: 6, fontSize: 11,
        background: 'rgba(255,255,255,.04)', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        maxHeight: open ? 460 : 92, overflow: 'auto', lineHeight: 1.5,
      }}>{text}{truncated && !open ? '\n…' : ''}</pre>
      {long && (
        <button type="button" onClick={() => setOpen((o) => !o)} style={{
          marginTop: 4, fontSize: 11, fontWeight: 600, cursor: 'pointer', border: 'none',
          background: 'transparent', color: 'var(--accent)', padding: 0,
        }}>{open ? 'Show less' : 'Show full query'}</button>
      )}
    </div>
  );
}

function GroupRow({ g, showVolume }: { g: SqlGroup; showVolume: boolean }) {
  const [open, setOpen] = useState(false);
  const ambiguous = g.ambiguousCallSites > 0;
  const looped = g.inLoopCallSites > 0;
  const accent = ambiguous ? '#fbbf24' : looped ? 'var(--accent)' : 'var(--border)';

  return (
    <div className="card" style={{ padding: '12px 14px', borderLeft: `3px solid ${accent}` }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {showVolume && (
          <div style={{ textAlign: 'center', minWidth: 74 }}>
            <div style={{ fontSize: 19, fontWeight: 700, lineHeight: 1.1 }}>
              {(g.estExecutions ?? 0).toLocaleString()}{g.estIsLowerBound ? '+' : ''}
            </div>
            <div className="muted" style={{ fontSize: 9.5, marginTop: 2 }}>est. runs</div>
          </div>
        )}
        <div style={{ textAlign: 'center', minWidth: 58 }}>
          <div style={{ fontSize: 16, fontWeight: 700, lineHeight: 1.1 }}>
            {g.exclusiveCallSites > 0
              ? <><span className="muted" style={{ fontSize: 13 }}>{g.callSites}</span><span style={{ color: '#a78bfa' }}>→{g.maxPerRun}</span></>
              : g.callSites}
          </div>
          <div className="muted" style={{ fontSize: 9.5, marginTop: 2 }}>
            {g.exclusiveCallSites > 0 ? 'sites → max/run' : `call site${g.callSites > 1 ? 's' : ''}`}
          </div>
        </div>
        <div style={{ flex: 1, minWidth: 240 }}>
          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', color: 'var(--accent)' }}>{g.statement || '?'}</span>
            {g.databases.map((d) => <Badge key={d} color={d === 'Engine_BMSS' ? '#f59e0b' : '#6b7280'}>{d}</Badge>)}
            {g.exclusiveCallSites > 0 && (
              <Badge color="#a78bfa" title={`${g.callSites} call sites, but mutually exclusive Switch/If branches mean at most ${g.maxPerRun} fire per run.`}>
                dispatcher {g.callSites}→{g.maxPerRun}
              </Badge>
            )}
            {looped && <Badge color="var(--accent)" title={`${g.inLoopCallSites} call site(s) inside a Foreach/Until — executes once per loop item, so the estimate is a lower bound`}>in loop ×{g.inLoopCallSites}</Badge>}
            {ambiguous && <Badge color="#fbbf24" title={`${g.ambiguousCallSites} call site(s) read a variable assigned in more than one place. The text shown is the first assignment; the actual statement may differ per run. Needs run-input inspection to confirm.`}>ambiguous ×{g.ambiguousCallSites}</Badge>}
            {!g.anyExact && !ambiguous && <Badge color="#6b7280" title="Shape recovered from a template with @{…} placeholders; literal values are only known at runtime.">template</Badge>}
          </div>
          {g.tables.length > 0 && (
            <div className="muted" style={{ fontSize: 11, marginTop: 5 }}>
              tables: {g.tables.slice(0, 8).join(', ')}{g.tables.length > 8 ? ` +${g.tables.length - 8}` : ''}
            </div>
          )}
          <SqlBlock text={g.exampleText} truncated={g.exampleTruncated} />
          <button type="button" onClick={() => setOpen((o) => !o)} style={{
            marginTop: 7, fontSize: 11.5, fontWeight: 600, cursor: 'pointer', border: 'none',
            background: 'transparent', color: 'var(--muted)', padding: 0, display: 'flex', alignItems: 'center', gap: 4,
          }}>
            <ChevronRight size={12} style={{ transform: open ? 'rotate(90deg)' : undefined, transition: 'transform .15s' }} />
            {g.distinctWorkflows} workflow{g.distinctWorkflows > 1 ? 's' : ''} · {g.textClasses.map((c) => TEXT_CLASS_LABEL[c] || c).join(', ')}
          </button>
          {open && (
            <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 3 }}>
              {g.workflows.map((w, i) => (
                <div key={`${w.workflow}/${w.action}/${i}`} style={{
                  display: 'flex', gap: 8, alignItems: 'center', fontSize: 11.5,
                  padding: '4px 8px', borderRadius: 6, background: 'rgba(255,255,255,.03)',
                }}>
                  <span style={{
                    width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
                    background: w.state === 'Enabled' ? 'var(--good)' : '#6b7280',
                  }} />
                  <span style={{ fontWeight: 600, wordBreak: 'break-word' }}>{w.workflow}</span>
                  <span className="muted" style={{ fontSize: 10.5 }}>{w.action.replace(/_/g, ' ')}</span>
                  {w.inLoop && <Badge color="var(--accent)">loop</Badge>}
                  {showVolume && w.runCount != null && (
                    <span className="muted" style={{ fontSize: 10.5, marginLeft: 'auto', whiteSpace: 'nowrap' }}>{w.runCount} runs</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function WorkflowVolumeTable({ rows }: { rows: SqlWorkflowVolume[] }) {
  const max = Math.max(1, ...rows.map((r) => r.estExecutions));
  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr style={{ background: 'rgba(255,255,255,.04)' }}>
            <th style={{ textAlign: 'left', padding: '8px 12px' }}>Workflow</th>
            <th style={{ textAlign: 'right', padding: '8px 8px' }}>Call sites</th>
            <th style={{ textAlign: 'right', padding: '8px 8px' }} title="Most call sites that can execute in a single run, after applying Switch/If exclusivity">Max/run</th>
            <th style={{ textAlign: 'right', padding: '8px 8px' }}>Runs</th>
            <th style={{ textAlign: 'right', padding: '8px 12px' }}>Est. queries</th>
            <th style={{ padding: '8px 12px', width: 150 }} />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.workflow} style={{ borderTop: '1px solid var(--border)' }}>
              <td style={{ padding: '7px 12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  <span style={{
                    width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
                    background: r.state === 'Enabled' ? 'var(--good)' : '#6b7280',
                  }} />
                  <span style={{ fontWeight: 600, wordBreak: 'break-word' }}>{r.workflow}</span>
                  {r.exclusiveCallSites > 0 && (
                    <Badge color="#a78bfa" title={`Dispatcher: ${r.callSites} call sites sit in mutually exclusive Switch/If branches, so at most ${r.maxPerRun} can fire per run. Counting all ${r.callSites} would overstate this row by ${(r.callSites / Math.max(1, r.maxPerRun)).toFixed(1)}×.`}>
                      dispatcher {r.callSites}→{r.maxPerRun}
                    </Badge>
                  )}
                  {r.inLoopCallSites > 0 && <Badge color="var(--accent)" title={`${r.inLoopCallSites} call site(s) inside a loop — real count is higher`}>loop ×{r.inLoopCallSites}</Badge>}
                </div>
              </td>
              <td style={{ textAlign: 'right', padding: '7px 8px', color: r.exclusiveCallSites > 0 ? 'var(--muted)' : undefined }}>{r.callSites}</td>
              <td style={{ textAlign: 'right', padding: '7px 8px', fontWeight: r.exclusiveCallSites > 0 ? 700 : 400, color: r.exclusiveCallSites > 0 ? '#a78bfa' : undefined }}>{r.maxPerRun}</td>
              <td style={{ textAlign: 'right', padding: '7px 8px' }}>
                {r.runCount}{r.runCountTruncated && <span title="Run-history page cap hit — actual run count is higher" style={{ color: '#fbbf24' }}>+</span>}
              </td>
              <td style={{ textAlign: 'right', padding: '7px 12px', fontWeight: 700 }}
                title={r.exclusiveCallSites > 0 ? `${r.estIfEveryBranchFired.toLocaleString()} if every branch fired` : undefined}>
                {r.estExecutions.toLocaleString()}{r.estIsLowerBound || r.runCountTruncated ? '+' : ''}
              </td>
              <td style={{ padding: '7px 12px' }}>
                <div style={{ height: 6, borderRadius: 3, background: 'rgba(255,255,255,.06)' }}>
                  <div style={{
                    width: `${Math.round((r.estExecutions / max) * 100)}%`, height: '100%', borderRadius: 3,
                    background: 'var(--accent)',
                  }} />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// No 30d option: measured 2026-08-04, a cold 30-day volume join takes ~44s
// against the ~45s SWA gateway timeout (and still leaves 2 workflows past the
// deep run-count cap). 14d lands at ~23s. Raising this needs the run counts
// moved to a background sweep, not a longer request.
const SQL_DAY_OPTIONS = [0, 1, 2, 7, 14];

function SqlCatalog() {
  const [data, setData] = useState<SqlCatalogResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [volumeError, setVolumeError] = useState<string | null>(null);
  const [days, setDays] = useState(7);
  const [db, setDb] = useState('Engine_BMSS');
  const [tab, setTab] = useState<'shapes' | 'workflows'>('workflows');
  const [q, setQ] = useState('');
  const [onlyLoops, setOnlyLoops] = useState(false);

  const [volumeLoading, setVolumeLoading] = useState(false);

  // Two-stage load. The catalog alone is one cached ARM call; the volume join
  // fans run-history counts over ~250 workflows and, cold, takes ~20s more.
  // Measured 2026-08-04: a single combined request was 34s cold against the
  // ~45s SWA gateway timeout. Splitting it renders the catalog immediately and
  // keeps each request well inside the limit.
  const load = useCallback((d: number, dbf: string, refresh: boolean, signal?: AbortSignal) => {
    const qs = (extra: string) => {
      const parts = ['mode=sqlcatalog'];
      if (dbf) parts.push(`db=${encodeURIComponent(dbf)}`);
      if (refresh) parts.push('refresh=1');
      if (extra) parts.push(extra);
      return `/api/logicapp-health?${parts.join('&')}`;
    };

    setLoading(true); setError(null); setVolumeLoading(false);
    getJson<SqlCatalogResult>(qs(''), signal)
      .then((r) => {
        setData(r);
        setLoading(false);
        if (d <= 0) return;
        setVolumeLoading(true);
        return getJson<SqlCatalogResult>(qs(`days=${d}`), signal)
          .then((v) => { setData(v); setVolumeLoading(false); })
          .catch((e) => {
            if (e.name === 'AbortError') return;
            // Keep the catalog on screen — only the volume overlay failed.
            setVolumeError(e.message);
            setVolumeLoading(false);
          });
      })
      .catch((e) => { if (e.name !== 'AbortError') { setError(e.message); setLoading(false); } });
  }, []);

  useEffect(() => {
    const ctrl = new AbortController();
    setVolumeError(null);
    load(days, db, false, ctrl.signal);
    return () => ctrl.abort();
  }, [days, db, load]);

  const showVolume = !!data?.volume;

  const groups = useMemo(() => {
    let list = data?.groups || [];
    if (onlyLoops) list = list.filter((g) => g.inLoopCallSites > 0);
    if (q.trim()) {
      const s = q.toLowerCase();
      list = list.filter((g) =>
        g.exampleText.toLowerCase().includes(s) ||
        g.tables.some((t) => t.includes(s)) ||
        g.workflows.some((w) => w.workflow.toLowerCase().includes(s))
      );
    }
    return list;
  }, [data, q, onlyLoops]);

  const wfRows = useMemo(() => {
    let list = data?.workflowVolume || [];
    if (onlyLoops) list = list.filter((w) => w.inLoopCallSites > 0);
    if (q.trim()) {
      const s = q.toLowerCase();
      list = list.filter((w) => w.workflow.toLowerCase().includes(s));
    }
    return list;
  }, [data, q, onlyLoops]);

  const t = data?.totals;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* controls */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 2, background: 'var(--border)', padding: 2, borderRadius: 8 }}>
          {(['workflows', 'shapes'] as const).map((k) => (
            <button key={k} type="button" onClick={() => setTab(k)} style={{
              fontSize: 12, fontWeight: 600, padding: '5px 11px', borderRadius: 6, cursor: 'pointer', border: 'none',
              background: tab === k ? 'var(--panel, #11161f)' : 'transparent',
              color: tab === k ? 'var(--accent)' : 'var(--muted)',
            }}>{k === 'workflows' ? 'By workflow' : 'By query shape'}</button>
          ))}
        </div>
        {(data?.databases || []).map((d) => (
          <button key={d.database} type="button" onClick={() => setDb(db === d.database ? '' : d.database)} style={{
            fontSize: 11.5, fontWeight: 600, padding: '5px 10px', borderRadius: 999, cursor: 'pointer',
            border: `1px solid ${db === d.database ? 'var(--accent)' : 'var(--border)'}`,
            background: db === d.database ? 'color-mix(in srgb, var(--accent) 14%, transparent)' : 'transparent',
            color: db === d.database ? 'var(--accent)' : 'var(--muted)',
          }}>{d.database} · {d.callSites}</button>
        ))}
        <div style={{ display: 'flex', gap: 2, background: 'var(--border)', padding: 2, borderRadius: 8 }}>
          {SQL_DAY_OPTIONS.map((d) => (
            <button key={d} type="button" onClick={() => setDays(d)} style={{
              fontSize: 12, fontWeight: 600, padding: '5px 10px', borderRadius: 6, cursor: 'pointer', border: 'none',
              background: days === d ? 'var(--panel, #11161f)' : 'transparent', color: days === d ? 'var(--accent)' : 'var(--muted)',
            }}>{d === 0 ? 'no volume' : `${d}d`}</button>
          ))}
        </div>
        <button className="btn" onClick={() => load(days, db, true)} disabled={loading} style={{ gap: 6, fontSize: 12.5 }}>
          <RefreshCw size={14} style={{ animation: loading ? 'spin 1s linear infinite' : undefined }} /> Refresh
        </button>
      </div>

      {/* KPIs */}
      {t && (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <Kpi label="SQL call sites" value={t.callSites} />
          <Kpi label="Max reachable / run" value={t.maxPerRunCallSites} accent="#a78bfa" />
          <Kpi label="Workflows w/ SQL" value={t.workflowsWithSql} />
          <Kpi label="Distinct shapes" value={t.distinctShapes} />
          <Kpi label="Dispatchers" value={t.dispatcherWorkflows} accent={t.dispatcherWorkflows > 0 ? '#a78bfa' : undefined} />
          <Kpi label="Inside a loop" value={t.inLoopCallSites} accent={t.inLoopCallSites > 0 ? 'var(--accent)' : undefined} />
          <Kpi label="Exact text" value={`${Math.round((t.exactTextCallSites / Math.max(1, t.callSites)) * 100)}%`} />
        </div>
      )}

      {/* estimate disclosure — deliberately not a tooltip */}
      {showVolume && data?.volume && (
        <div className="card" style={{ padding: '11px 14px', borderLeft: '3px solid #fbbf24' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12.5, fontWeight: 700, color: '#fbbf24' }}>
            <AlertTriangle size={14} /> &ldquo;Est. queries&rdquo; is an estimate, not a measurement
          </div>
          <div className="muted" style={{ fontSize: 11.5, marginTop: 5, lineHeight: 1.6 }}>
            Basis: {data.volume.basis} over the last {data.volume.days} day(s).
            <ul style={{ margin: '5px 0 0', paddingLeft: 18 }}>
              {data.volume.caveats.map((c, i) => <li key={i}>{c}</li>)}
            </ul>
            Rows marked <strong>+</strong> are lower bounds. Exact per-execution counts need per-run action
            status and loop-repetition expansion (Phase 2).
          </div>
        </div>
      )}

      {/* filters */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: '1 1 240px', maxWidth: 380 }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--muted)' }} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter by SQL text, table, or workflow…"
            style={{ width: '100%', padding: '8px 10px 8px 32px', borderRadius: 8, border: '1px solid var(--border)', background: 'transparent', color: 'inherit', fontSize: 13 }} />
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}>
          <input type="checkbox" checked={onlyLoops} onChange={(e) => setOnlyLoops(e.target.checked)} />
          Only call sites inside loops
        </label>
        <span className="muted" style={{ fontSize: 12, marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
          {volumeLoading && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 5, color: 'var(--accent)' }}>
              <RefreshCw size={12} style={{ animation: 'spin 1s linear infinite' }} /> counting runs…
            </span>
          )}
          {tab === 'shapes' ? `${groups.length} of ${data?.groupCount ?? 0} shapes` : `${wfRows.length} of ${data?.workflowVolume?.length ?? 0} workflows`}
          {data?.fromCache && <> · cached {data.cacheAgeSec}s ago</>}
        </span>
      </div>

      {volumeError && (
        <div className="card" style={{ padding: '10px 14px', borderLeft: '3px solid #f59e0b' }}>
          <div style={{ fontSize: 12.5, color: '#f59e0b', display: 'flex', alignItems: 'center', gap: 6 }}>
            <AlertTriangle size={13} /> Volume estimate unavailable — showing call sites only
          </div>
          <div className="muted" style={{ fontSize: 11.5, marginTop: 4 }}>{volumeError}</div>
        </div>
      )}

      {/* body */}
      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="card" style={{ height: 92, animation: 'pulse 1.4s ease-in-out infinite', animationDelay: `${i * 0.08}s` }} />
          ))}
        </div>
      ) : error ? (
        <div className="card" style={{ padding: 20, borderLeft: '3px solid var(--bad)' }}>
          <div style={{ color: 'var(--bad)', fontWeight: 600, marginBottom: 6 }}>Could not build the SQL catalog</div>
          <div className="muted" style={{ fontSize: 12.5 }}>{error}</div>
        </div>
      ) : tab === 'workflows' ? (
        showVolume ? (
          <WorkflowVolumeTable rows={wfRows} />
        ) : volumeLoading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div className="muted" style={{ fontSize: 12.5, display: 'flex', alignItems: 'center', gap: 6 }}>
              <RefreshCw size={13} style={{ animation: 'spin 1s linear infinite' }} />
              Counting runs across {data?.totals.workflowsWithSql ?? 0} workflows — this takes ~20s cold, then caches for 5 minutes.
            </div>
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="card" style={{ height: 32, animation: 'pulse 1.4s ease-in-out infinite', animationDelay: `${i * 0.07}s` }} />
            ))}
          </div>
        ) : (
          <div className="muted" style={{ padding: '30px 0', textAlign: 'center', fontSize: 12.5 }}>
            Pick a day window above to rank workflows by estimated query volume.
          </div>
        )
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {groups.map((g) => <GroupRow key={g.fingerprint} g={g} showVolume={showVolume} />)}
          {groups.length === 0 && <div className="muted" style={{ textAlign: 'center', padding: '40px 0' }}>No query shapes match.</div>}
        </div>
      )}

      {/* unresolved — surface rather than silently drop */}
      {data && data.unresolved.length > 0 && (
        <div className="card" style={{ padding: '11px 14px', borderLeft: '3px solid #6b7280' }}>
          <div style={{ fontSize: 12.5, fontWeight: 700 }}>{data.unresolved.length} call site(s) with no recoverable SQL text</div>
          <div className="muted" style={{ fontSize: 11.5, marginTop: 4 }}>
            {data.unresolved.slice(0, 12).map((u) => `${u.workflow}/${u.action} [${u.textClass}]`).join(' · ')}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- main page ----------
type SortKey = 'failed' | 'duration' | 'name' | 'runs';
type FilterKey = 'all' | 'issues' | 'long' | 'failing';

export default function LogicAppReport() {
  const [view, setView] = useState<'overview' | 'search' | 'sqlcatalog'>('overview');
  const [days, setDays] = useState(2);
  const [data, setData] = useState<Overview | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<FilterKey>('all');
  const [sort, setSort] = useState<SortKey>('failed');
  const [selected, setSelected] = useState<AppSummary | null>(null);

  const load = useCallback((d: number, refresh: boolean, signal?: AbortSignal) => {
    if (refresh) setRefreshing(true); else setLoading(true);
    setError(null);
    getJson<Overview>(`/api/logicapp-health?mode=overview&days=${d}${refresh ? '&refresh=1' : ''}`, signal)
      .then((o) => { setData(o); setLoading(false); setRefreshing(false); })
      .catch((e) => { if (e.name !== 'AbortError') { setError(e.message); setLoading(false); setRefreshing(false); } });
  }, []);

  useEffect(() => {
    const ctrl = new AbortController();
    load(days, false, ctrl.signal);
    return () => ctrl.abort();
  }, [days, load]);

  const apps = data?.apps || [];
  const visible = useMemo(() => {
    let list = apps;
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((a) => a.name.toLowerCase().includes(q) || (a.summary || '').toLowerCase().includes(q));
    }
    if (filter === 'issues') list = list.filter((a) => (a.failed || 0) > 0 || a.isLongRunning || a.error);
    else if (filter === 'long') list = list.filter((a) => a.isLongRunning);
    else if (filter === 'failing') list = list.filter((a) => (a.failed || 0) > 0);
    const arr = [...list];
    arr.sort((a, b) => {
      switch (sort) {
        case 'failed': return (b.failed || 0) - (a.failed || 0) || (b.maxDurationSec || 0) - (a.maxDurationSec || 0);
        case 'duration': return (b.maxDurationSec || 0) - (a.maxDurationSec || 0);
        case 'runs': return (b.runCount || 0) - (a.runCount || 0);
        case 'name': return a.name.localeCompare(b.name);
        default: return 0;
      }
    });
    return arr;
  }, [apps, search, filter, sort]);

  const t = data?.totals;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {/* header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Activity size={20} style={{ color: 'var(--accent)' }} />
            <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: '-.01em' }}>Logic App Health</div>
          </div>
          {view === 'overview' && (
            <div className="muted" style={{ fontSize: 13, marginTop: 3 }}>
              Live from Azure · last {days} day{days > 1 ? 's' : ''}
              {data && <> · {data.fromCache ? `cached ${data.cacheAgeSec}s ago` : 'fresh'} · swept {data.totals.apps} enabled apps</>}
            </div>
          )}
          {view === 'search' && (
            <div className="muted" style={{ fontSize: 13, marginTop: 3 }}>Search across all Logic App definitions · live from Azure, cached 30 min</div>
          )}
          {view === 'sqlcatalog' && (
            <div className="muted" style={{ fontSize: 13, marginTop: 3 }}>
              Every SQL-connector call site, grouped by query shape · from definitions only, cached 30 min
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {/* view tabs */}
          <div style={{ display: 'flex', gap: 2, background: 'var(--border)', padding: 2, borderRadius: 8 }}>
            <button type="button" onClick={() => setView('overview')} style={{
              fontSize: 12, fontWeight: 600, padding: '5px 11px', borderRadius: 6, cursor: 'pointer', border: 'none',
              background: view === 'overview' ? 'var(--panel, #11161f)' : 'transparent',
              color: view === 'overview' ? 'var(--accent)' : 'var(--muted)',
              display: 'flex', alignItems: 'center', gap: 5,
            }}><Activity size={12} /> Overview</button>
            <button type="button" onClick={() => setView('search')} style={{
              fontSize: 12, fontWeight: 600, padding: '5px 11px', borderRadius: 6, cursor: 'pointer', border: 'none',
              background: view === 'search' ? 'var(--panel, #11161f)' : 'transparent',
              color: view === 'search' ? 'var(--accent)' : 'var(--muted)',
              display: 'flex', alignItems: 'center', gap: 5,
            }}><Search size={12} /> Definition Search</button>
            <button type="button" onClick={() => setView('sqlcatalog')} style={{
              fontSize: 12, fontWeight: 600, padding: '5px 11px', borderRadius: 6, cursor: 'pointer', border: 'none',
              background: view === 'sqlcatalog' ? 'var(--panel, #11161f)' : 'transparent',
              color: view === 'sqlcatalog' ? 'var(--accent)' : 'var(--muted)',
              display: 'flex', alignItems: 'center', gap: 5,
            }}><Database size={12} /> SQL Call Sites</button>
          </div>
          {view === 'overview' && (
            <>
              <div style={{ display: 'flex', gap: 2, background: 'var(--border)', padding: 2, borderRadius: 8 }}>
                {DAY_OPTIONS.map((d) => (
                  <button key={d} type="button" onClick={() => setDays(d)} style={{
                    fontSize: 12, fontWeight: 600, padding: '5px 11px', borderRadius: 6, cursor: 'pointer', border: 'none',
                    background: days === d ? 'var(--panel, #11161f)' : 'transparent', color: days === d ? 'var(--accent)' : 'var(--muted)',
                  }}>{d}d</button>
                ))}
              </div>
              <button className="btn" onClick={() => load(days, true)} disabled={refreshing || loading} style={{ gap: 6, fontSize: 12.5 }}>
                <RefreshCw size={14} style={{ animation: refreshing ? 'spin 1s linear infinite' : undefined }} /> Refresh
              </button>
            </>
          )}
        </div>
      </div>

      {view === 'search' ? (
        <DefSearch />
      ) : view === 'sqlcatalog' ? (
        <SqlCatalog />
      ) : (
        <>
          {/* KPI bar */}
          {t && (
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <Kpi label="Enabled apps" value={t.apps} />
              <Kpi label={`Runs (${days}d)`} value={t.runs.toLocaleString()} />
              <Kpi label="Failures" value={t.failed} accent={t.failed > 0 ? 'var(--bad)' : undefined} icon={t.failed > 0 ? <XCircle size={15} style={{ color: 'var(--bad)' }} /> : undefined} />
              <Kpi label="Apps w/ failures" value={t.appsWithFailures} accent={t.appsWithFailures > 0 ? '#fbbf24' : undefined} />
              <Kpi label="Long-running (>10m)" value={t.appsLongRunning} accent={t.appsLongRunning > 0 ? '#fbbf24' : undefined} icon={t.appsLongRunning > 0 ? <Timer size={15} style={{ color: '#fbbf24' }} /> : undefined} />
              <Kpi label="Longest run" value={fmtDur(t.longestRunSec)} />
            </div>
          )}

          {/* controls */}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={{ position: 'relative', flex: '1 1 220px', maxWidth: 320 }}>
              <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--muted)' }} />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search apps…"
                style={{ width: '100%', padding: '8px 10px 8px 32px', borderRadius: 8, border: '1px solid var(--border)', background: 'transparent', color: 'inherit', fontSize: 13 }} />
            </div>
            <div style={{ display: 'flex', gap: 2, background: 'var(--border)', padding: 2, borderRadius: 8 }}>
              {([['all', 'All'], ['issues', 'Issues'], ['failing', 'Failing'], ['long', 'Long-running']] as [FilterKey, string][]).map(([k, lbl]) => (
                <button key={k} type="button" onClick={() => setFilter(k)} style={{
                  fontSize: 12, fontWeight: 600, padding: '5px 11px', borderRadius: 6, cursor: 'pointer', border: 'none',
                  background: filter === k ? 'var(--panel, #11161f)' : 'transparent', color: filter === k ? 'var(--accent)' : 'var(--muted)',
                }}>{lbl}</button>
              ))}
            </div>
            <select value={sort} onChange={(e) => setSort(e.target.value as SortKey)}
              style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'transparent', color: 'inherit', fontSize: 12.5 }}>
              <option value="failed">Sort: Failures</option>
              <option value="duration">Sort: Max duration</option>
              <option value="runs">Sort: Run count</option>
              <option value="name">Sort: Name</option>
            </select>
            <span className="muted" style={{ fontSize: 12, marginLeft: 'auto' }}>{visible.length} of {apps.length}</span>
          </div>

          {/* body */}
          {loading ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 14 }}>
              {Array.from({ length: 9 }).map((_, i) => (
                <div key={i} className="card" style={{ height: 150, borderLeft: '3px solid rgba(122,162,255,.2)', animation: 'pulse 1.4s ease-in-out infinite', animationDelay: `${i * 0.08}s` }} />
              ))}
            </div>
          ) : error ? (
            <div className="card" style={{ padding: 20, borderLeft: '3px solid var(--bad)' }}>
              <div style={{ color: 'var(--bad)', fontWeight: 600, marginBottom: 6 }}>Could not load Logic App health</div>
              <div className="muted" style={{ fontSize: 12.5 }}>{error}</div>
              <div className="muted" style={{ fontSize: 11.5, marginTop: 10 }}>
                If this says the identity can't get a token, set the <code>AZURE_TENANT_ID</code> / <code>AZURE_CLIENT_ID</code> / <code>AZURE_CLIENT_SECRET</code> app settings in the SWA (the service principal needs <strong>Logic App Reader</strong> on the resource group). Locally, an <code>az login</code> session is used.
              </div>
            </div>
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 14 }}>
                {visible.map((app) => <AppCard key={app.name} app={app} onOpen={setSelected} />)}
              </div>
              {visible.length === 0 && <div className="muted" style={{ textAlign: 'center', padding: '40px 0' }}>No apps match.</div>}
            </>
          )}

          {selected && <DrillDrawer app={selected} days={days} onClose={() => setSelected(null)} />}
        </>
      )}

      <style>{`
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.4} }
        @keyframes spin { to { transform: rotate(360deg); } }
        .logicapp-md table { border-collapse: collapse; width: 100%; margin: 8px 0; }
        .logicapp-md th, .logicapp-md td { border: 1px solid var(--border); padding: 5px 8px; text-align: left; vertical-align: top; }
        .logicapp-md th { background: rgba(255,255,255,.04); }
        .logicapp-md h1 { font-size: 16px; } .logicapp-md h2 { font-size: 14px; margin-top: 14px; }
        .logicapp-md code { background: rgba(255,255,255,.07); padding: 1px 5px; border-radius: 4px; font-size: 11.5px; }
        .logicapp-md a { color: var(--accent); }
        .logicapp-md ul { padding-left: 18px; }
      `}</style>
    </div>
  );
}
